/**
 * Pure formatting + escaping helpers for the receipt template.
 *
 * Kept separate from the template so they're trivially unit-testable — the
 * template is a single big string-concatenation function that's hard to test
 * holistically; the helpers can be pinned individually.
 */

const HTML_ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/**
 * HTML-escape a value for safe interpolation into the receipt template.
 *
 * Why we escape every dynamic piece (item names, customer names, etc.):
 *   - Item names in egydemo1 contain ampersands and quotes (e.g. "Burger & Fries").
 *   - Customer free-form fields can contain anything a cashier types.
 *   - We DO render `printConfig.receiptFooterHtml` un-escaped (it's a
 *     tenant-admin-authored field), but everything else flows through here.
 */
export function escapeHtml(value: string | number | null | undefined): string {
  if (value == null) return '';
  return String(value).replace(/[&<>"']/g, (ch) => HTML_ESCAPE_MAP[ch] ?? ch);
}

/**
 * Choose between the Arabic and English variant of a label.
 *
 * Strategy: prefer Arabic where present (target tenants are Arabic-primary),
 * fall back to English, fall back to the empty string. Either input may be
 * `null`, `undefined`, or whitespace.
 */
export function pickName(
  en: string | null | undefined,
  ar: string | null | undefined
): string {
  const trimAr = typeof ar === 'string' ? ar.trim() : '';
  if (trimAr.length > 0) return trimAr;
  const trimEn = typeof en === 'string' ? en.trim() : '';
  return trimEn;
}

/**
 * Format a money amount with the currency's precision and a trailing code.
 *
 * Accepts either a plain ISO-like code string (e.g. 'EGP', defaults to 2
 * decimals) or a `{ code, precision }` object pulled from PrintConfig.
 * Latin numerals everywhere (even on Arabic receipts) — Egyptian and Gulf
 * thermal receipts almost universally use 0-9 for amounts; Arabic-Indic
 * digits are reserved for formal/government documents.
 */
export function formatMoney(
  amount: number,
  currency: string | { code: string; precision?: number } = 'EGP',
): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  const code = typeof currency === 'string' ? currency : currency.code;
  const precision = typeof currency === 'string' ? 2 : (currency.precision ?? 2);
  const digits = Math.max(0, Math.min(8, precision));
  return `${safe.toFixed(digits)} ${code}`;
}

/**
 * The same amount with NO currency code — "13.500", not "13.500 KWD".
 *
 * For a custom design whose page says the code does not belong on the paper:
 * a single-currency shop naming its currency on every line of every ticket is
 * noise, and the slips those tenants are reproducing do not do it. The code is
 * a page-level choice precisely so it cannot be made line by line and leave
 * half a receipt labelled.
 */
export function formatAmount(
  amount: number,
  currency: string | { code: string; precision?: number } = 'EGP',
): string {
  const safe = Number.isFinite(amount) ? amount : 0;
  const precision = typeof currency === 'string' ? 2 : currency.precision ?? 2;
  return safe.toFixed(Math.max(0, Math.min(8, precision)));
}

/**
 * Format a quantity. Whole numbers display without trailing zeros (e.g. "2"
 * not "2.000"); fractional quantities show up to three decimals trimmed.
 *
 * Why three: the legacy `Inv_Receipt_d.Qty` is decimal(18,8) and many tenants
 * sell by weight — but printing eight decimals makes a thermal line wrap
 * unreadable. Three is the established compromise from the legacy ERP itself.
 */
export function formatQty(qty: number): string {
  if (!Number.isFinite(qty)) return '0';
  if (Number.isInteger(qty)) return String(qty);
  return qty
    .toFixed(3)
    .replace(/0+$/, '')
    .replace(/\.$/, '');
}

/**
 * Format the order entry datetime for the receipt template.
 *
 * Backend ships `entry_date` as a NAIVE wall-clock string in tenant TZ
 * (e.g. '2026-05-08T08:18:43.000'). We parse the components straight
 * from the string — `new Date(naive)` would re-interpret as host-local
 * time and could double-shift on devices whose system clock isn't
 * configured for the tenant region.
 *
 * Output: "YYYY-MM-DD HH:MM AM/PM", or "YYYY-MM-DD HH:MM ص/م" on an Arabic
 * receipt. Only the meridiem is translated: the DIGITS stay Latin (0-9) and
 * the date keeps its ISO-ish order, because that is what Gulf and Egyptian
 * thermal receipts print and what a customer comparing a slip to a bank
 * statement expects. Arabic-Indic digits are for formal documents, not tills —
 * the same rule `formatMoney` follows for amounts.
 */
const NAIVE_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::\d{2}(?:\.\d{1,7})?)?$/;

/** The same shape, keeping the SECONDS the one above deliberately discards. */
const NAIVE_DATETIME_SEC_RE =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,7})?)?$/;

/**
 * An entry date split into its wall-clock parts.
 *
 * `formatEntryDate` renders ONE house format; a custom receipt design needs
 * the pieces, because the slip it is reproducing writes the date its own way
 * ("28-08-2024" on nojoom_live's ticket) and the time to the second.
 *
 * It parses the naive tenant wall-clock string directly rather than going
 * through `Date`, for the same reason `formatEntryDate` does: the backend
 * already resolved the tenant's timezone, and re-parsing through `Date` shifts
 * the value by the till's own offset. Falling back to `Date` only covers the
 * strings that are not in the naive shape. Returns null when nothing parses.
 */
export interface EntryDateParts {
  year: number; month: number; day: number;
  hour24: number; minute: number; second: number;
}

export function entryDateParts(entryDate: string): EntryDateParts | null {
  const trimmed = (entryDate ?? '').trim();
  if (!trimmed) return null;
  const m = NAIVE_DATETIME_SEC_RE.exec(trimmed);
  if (m) {
    return {
      year: Number(m[1]), month: Number(m[2]), day: Number(m[3]),
      hour24: Number(m[4]), minute: Number(m[5]), second: Number(m[6] ?? 0),
    };
  }
  const ms = Date.parse(trimmed);
  if (Number.isNaN(ms)) return null;
  const d = new Date(ms);
  return {
    year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(),
    hour24: d.getHours(), minute: d.getMinutes(), second: d.getSeconds(),
  };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/** "28-08-2024" — day first, the order the legacy slip prints. */
export function formatDateDmy(p: EntryDateParts): string {
  return `${pad2(p.day)}-${pad2(p.month)}-${p.year}`;
}

/** "2024-08-28" — the house order, sortable. */
export function formatDateYmd(p: EntryDateParts): string {
  return `${p.year}-${pad2(p.month)}-${pad2(p.day)}`;
}

/**
 * "11:29:38 am" — 12-hour, to the second, LOWER-CASE meridiem.
 *
 * Lower case because that is what the legacy Sismatix ticket printed and what
 * this token exists to reproduce; `formatEntryDate`'s own "11:29 AM" is
 * untouched and remains the house format everywhere else.
 */
export function formatTimeFull(p: EntryDateParts, arabic = false): string {
  const suffix = arabic ? (p.hour24 >= 12 ? 'م' : 'ص') : p.hour24 >= 12 ? 'pm' : 'am';
  const h12 = p.hour24 % 12 === 0 ? 12 : p.hour24 % 12;
  return `${pad2(h12)}:${pad2(p.minute)}:${pad2(p.second)} ${suffix}`;
}

export function formatEntryDate(entryDate: string, arabic = false): string {
  const trimmed = (entryDate ?? '').trim();
  if (!trimmed) return '';
  const naive = NAIVE_DATETIME_RE.exec(trimmed);
  let year: number, month: number, day: number, hour24: number, minute: number;
  if (naive) {
    year = Number(naive[1]); month = Number(naive[2]); day = Number(naive[3]);
    hour24 = Number(naive[4]); minute = Number(naive[5]);
  } else {
    const ms = Date.parse(trimmed);
    if (Number.isNaN(ms)) return trimmed;
    const d = new Date(ms);
    year = d.getFullYear(); month = d.getMonth() + 1; day = d.getDate();
    hour24 = d.getHours(); minute = d.getMinutes();
  }
  const suffix = arabic
    ? (hour24 >= 12 ? 'م' : 'ص')
    : (hour24 >= 12 ? 'PM' : 'AM');
  const h12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(h12).padStart(2, '0')}:${String(minute).padStart(2, '0')} ${suffix}`;
}
